// btw-front/src/components/admin/DealOwnerChangeModal.jsx
// 관리자 딜 담당자 변경 팝업 — 파트너사 선택(활성 회사 목록) → 그 회사 영업대표 선택(→ 소유 계정 없는 영업대표면 담당 계정 선택).
// 저장은 PUT /api/admin/deals/:id/owner. 메시지·첨부 파일까지 딜 전체가 새 파트너사로 넘어가고
// 기존 파트너사는 더 이상 볼 수 없다. 최초 등록자 기록은 서버가 그대로 둔다.
//   props: open, deal(관리자 딜 응답), onClose(), onChanged() — 성공 시 부모가 재조회한다.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, message } from "antd";
import * as adminPartners from "../../api/adminPartners";
import { changeOwner } from "../../api/adminDeals";

const selectCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:bg-slate-50";

const errorMessage = (err, fallback) =>
  (err && err.payload && err.payload.error && err.payload.error.message) || fallback;

export default function DealOwnerChangeModal({ open, deal, onClose, onChanged }) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [reps, setReps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [repsLoading, setRepsLoading] = useState(false);
  const [repId, setRepId] = useState("");
  const [userId, setUserId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 로드 effect 는 t 에 의존하지 않는다 — t 가 바뀔 때마다 목록을 다시 부르지 않도록 ref 로 읽는다
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  // 열릴 때마다 초기화 + 파트너사 목록(활성만) 로드
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPartnerId("");
    setReps([]);
    setAccounts([]);
    setRepId("");
    setUserId("");
    setPartnersLoading(true);
    adminPartners
      .list()
      .then((items) => {
        if (!cancelled) setPartners(items.filter((p) => p.status === "active" && !p.isDeleted));
      })
      .catch((err) => {
        if (!cancelled)
          message.error(errorMessage(err, tRef.current("admin.deals.ownerChange.loadFailed")));
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // 파트너사를 고르면 그 회사 영업대표·활성 계정 로드
  useEffect(() => {
    setRepId("");
    setUserId("");
    setReps([]);
    setAccounts([]);
    if (!partnerId) return undefined;
    let cancelled = false;
    setRepsLoading(true);
    adminPartners
      .salesReps(partnerId)
      .then((res) => {
        if (cancelled) return;
        setReps(res.salesReps || []);
        setAccounts(res.accounts || []);
      })
      .catch((err) => {
        if (!cancelled)
          message.error(errorMessage(err, tRef.current("admin.deals.ownerChange.loadFailed")));
      })
      .finally(() => {
        if (!cancelled) setRepsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  const rep = reps.find((r) => r.id === repId) || null;
  // 소유 계정이 없는 옛 공용 영업대표 — 담당 계정을 함께 골라야 한다
  const needsAccount = !!rep && !rep.ownerUserId;
  const canSubmit = !!partnerId && !!rep && (!needsAccount || !!userId) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changeOwner(deal.id, {
        partnerId,
        partnerSalesRepId: repId,
        ...(needsAccount ? { partnerUserId: userId } : {}),
      });
      message.success(t("admin.deals.ownerChange.done"));
      onChanged();
    } catch (err) {
      message.error(errorMessage(err, t("admin.deals.ownerChange.failed")));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  const currentCompany = (deal && (deal.partnerCompanyName || deal.displayCompanyName)) || "-";

  return (
    <Modal
      title={t("admin.deals.ownerChange.title")}
      open={open}
      width={520}
      onCancel={close}
      closable={!submitting}
      keyboard={!submitting}
      maskClosable={!submitting}
      destroyOnClose
      footer={[
        <button
          key="cancel"
          type="button"
          onClick={close}
          disabled={submitting}
          className="btw-btn-ghost mr-2 disabled:opacity-60"
        >
          {t("admin.deals.ownerChange.cancel")}
        </button>,
        <button
          key="ok"
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="btw-btn-accent disabled:opacity-60"
        >
          {submitting ? t("admin.deals.ownerChange.submitting") : t("admin.deals.ownerChange.ok")}
        </button>,
      ]}
    >
      {deal && (
        <div className="space-y-4 text-sm">
          {/* 현재 담당 */}
          <dl className="grid grid-cols-[6rem_1fr] gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs">
            <dt className="text-slate-500">{t("admin.deals.ownerChange.currentPartner")}</dt>
            <dd className="text-slate-800">{currentCompany}</dd>
            <dt className="text-slate-500">{t("admin.deals.ownerChange.currentSalesRep")}</dt>
            <dd className="text-slate-800">{deal.salesRepName || "-"}</dd>
          </dl>

          {/* 1) 파트너사 */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-700" htmlFor="owner-partner">
              {t("admin.deals.ownerChange.partner")}
            </label>
            <select
              id="owner-partner"
              className={selectCls}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={submitting || partnersLoading}
            >
              <option value="">
                {partnersLoading
                  ? t("admin.deals.ownerChange.loading")
                  : t("admin.deals.ownerChange.partnerSelect")}
              </option>
              {partners.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.companyName}
                  {String(p.id) === String(deal.partnerId)
                    ? ` (${t("admin.deals.ownerChange.currentMark")})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          {/* 2) 영업대표 */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-700" htmlFor="owner-rep">
              {t("admin.deals.ownerChange.salesRep")}
            </label>
            <select
              id="owner-rep"
              className={selectCls}
              value={repId}
              onChange={(e) => {
                setRepId(e.target.value);
                setUserId("");
              }}
              disabled={submitting || !partnerId || repsLoading || reps.length === 0}
            >
              <option value="">
                {repsLoading
                  ? t("admin.deals.ownerChange.loading")
                  : t("admin.deals.ownerChange.salesRepSelect")}
              </option>
              {reps.map((r) => (
                // 소유 계정이 정지·삭제된 영업대표는 고를 수 없다(서버도 같은 기준으로 거부)
                <option key={r.id} value={r.id} disabled={!!r.ownerUserId && !r.isOwnerActive}>
                  {r.name}
                  {r.phone ? ` · ${r.phone}` : ""}
                  {r.ownerUserId && !r.isOwnerActive
                    ? ` (${t("admin.deals.ownerChange.ownerInactive")})`
                    : ""}
                </option>
              ))}
            </select>
            {partnerId && !repsLoading && reps.length === 0 && (
              <p className="text-xs text-rose-600">{t("admin.deals.ownerChange.noSalesRep")}</p>
            )}
            {rep && rep.ownerUserId && (
              <p className="text-xs text-slate-500">
                {t("admin.deals.ownerChange.ownerAccount", {
                  name: rep.ownerName || "-",
                  loginId: rep.ownerLoginId || "-",
                })}
              </p>
            )}
          </div>

          {/* 3) 담당 계정 — 소유 계정 없는 옛 영업대표만 */}
          {needsAccount && (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700" htmlFor="owner-account">
                {t("admin.deals.ownerChange.account")}
              </label>
              <select
                id="owner-account"
                className={selectCls}
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={submitting || accounts.length === 0}
              >
                <option value="">{t("admin.deals.ownerChange.accountSelect")}</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.loginId ? ` (${a.loginId})` : ""}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                {accounts.length === 0
                  ? t("admin.deals.ownerChange.noAccount")
                  : t("admin.deals.ownerChange.accountHint")}
              </p>
            </div>
          )}

          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t("admin.deals.ownerChange.warning")}
          </p>
        </div>
      )}
    </Modal>
  );
}
